import React, { useState, useEffect } from "react";
import { toast } from "sonner";
import { Trophy, Save } from "lucide-react";
import { api, formatError, resolveAsset } from "../lib/api";

function initials(n) {
  return (n || "?").split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();
}

export default function MatchEditor({ match, teamA, teamB, onSaved }) {
  const [scoreA, setScoreA] = useState(match.score_a || 0);
  const [scoreB, setScoreB] = useState(match.score_b || 0);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setScoreA(match.score_a || 0);
    setScoreB(match.score_b || 0);
  }, [match]);

  const save = async (winner_id = undefined) => {
    setBusy(true);
    try {
      const payload = {
        score_a: Number(scoreA) || 0,
        score_b: Number(scoreB) || 0,
        status: "completed",
      };
      if (winner_id !== undefined) payload.winner_id = winner_id;
      const { data } = await api.put(`/matches/${match.id}`, payload);
      toast.success(winner_id ? "Winner set — advanced to next round" : "Scores updated");
      onSaved && onSaved(data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || e.message);
    }
    setBusy(false);
  };

  const pickWinner = async (tid) => {
    if (!tid) return;
    // If scores equal, bump winner to 1 if all zero
    if (Number(scoreA) === Number(scoreB)) {
      if (tid === match.team_a_id) setScoreA((s) => Math.max(Number(s) + 1, 1));
      else setScoreB((s) => Math.max(Number(s) + 1, 1));
    }
    await save(tid);
  };

  return (
    <div data-testid={`match-editor-${match.id}`} className="space-y-3">
      <TeamLine team={teamA} score={scoreA} setScore={setScoreA} winner={match.winner_id} />
      <TeamLine team={teamB} score={scoreB} setScore={setScoreB} winner={match.winner_id} />
      <div className="flex gap-2 pt-2 flex-wrap">
        <button
          onClick={() => pickWinner(match.team_a_id)}
          disabled={busy || !match.team_a_id}
          data-testid={`set-winner-a-${match.id}`}
          className="btn-liquid !py-2 !px-4 !text-xs flex-1"
        >
          <Trophy size={12} /> {teamA ? `${teamA.name} wins` : "Set winner A"}
        </button>
        <button
          onClick={() => pickWinner(match.team_b_id)}
          disabled={busy || !match.team_b_id}
          data-testid={`set-winner-b-${match.id}`}
          className="btn-liquid !py-2 !px-4 !text-xs flex-1"
        >
          <Trophy size={12} /> {teamB ? `${teamB.name} wins` : "Set winner B"}
        </button>
      </div>
      <button
        onClick={() => save()}
        disabled={busy}
        data-testid={`save-scores-${match.id}`}
        className="btn-ghost-glass !py-2 !px-4 !text-xs w-full"
      >
        <Save size={12} /> {busy ? "Saving..." : "Save scores only"}
      </button>
    </div>
  );
}

function TeamLine({ team, score, setScore, winner }) {
  const isWin = winner && team && team.id === winner;
  return (
    <div className={`flex items-center gap-3 p-3 rounded-xl ${isWin ? "glass-red" : "glass-dark"}`}>
      {team?.logo_url ? (
        <img src={resolveAsset(team.logo_url)} alt="" className="h-8 w-8 rounded-full object-cover border border-white/10" />
      ) : (
        <div className="h-8 w-8 rounded-full flex items-center justify-center font-display font-bold text-xs" style={{ background: team?.color || "#2a2a2a" }}>
          {team ? initials(team.name) : "?"}
        </div>
      )}
      <span className={`flex-1 font-semibold truncate ${isWin ? "text-white" : "text-zinc-300"}`}>
        {team?.name || <span className="text-zinc-600">TBD</span>}
      </span>
      <input
        type="number"
        value={score}
        onChange={(e) => setScore(e.target.value)}
        disabled={!team}
        className="w-16 bg-black/50 border border-white/10 rounded-lg px-2 py-1.5 text-center font-mono font-bold text-lg focus:outline-none focus:border-red-500"
      />
    </div>
  );
}
